/** Post-purchase: welcome + dashboard setup, or thank-you only. */
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { ScrollView } from 'react-native';
import { Button, XStack, YStack } from 'tamagui';
import { FindDashboardSurveyModal } from '@/components/courses/FindDashboardSurveyModal';
import Container from '@/components/ui/Container';
import { BodyText, CaptionText, PageTitle, SubtitleText } from '@/components/ui/Typography';
import { BRAND_COLORS as C } from '@/lib/brandColors';
import type { AppleSubscription } from '@/lib/purchases/subscriptionStatus';

const SUCCESS = '#00B176';

type BaseProps = {
  planLabel: string | null;
  nextPayment: string | null;
  purchased: AppleSubscription | null;
};

type ThankYouProps = BaseProps & {
  mode: 'thank-you';
  title: string;
  message?: string;
  paymentCaption?: string | null;
  planSchedule?: {
    currentPlan: string;
    newPlan: string;
    switchDate: string;
  };
  effectiveAt?: string;
  billingHighlight?: { prefix: string; date: string };
  primaryLabel: string;
  onPrimary: () => void;
};

type OnboardingProps = BaseProps & {
  mode: 'onboarding';
  onComplete: () => void;
  onSkip: () => void;
};

export type PostPurchaseSuccessProps = ThankYouProps | OnboardingProps;

export function PostPurchaseSuccess(props: PostPurchaseSuccessProps) {
  if (props.mode === 'onboarding') {
    return <OnboardingScreen {...props} />;
  }
  return <ThankYouScreen {...props} />;
}

function ThankYouScreen({
  title,
  message,
  paymentCaption,
  planLabel,
  planSchedule,
  effectiveAt,
  billingHighlight,
  primaryLabel,
  onPrimary,
}: ThankYouProps) {
  return (
    <YStack f={1} bg={C.pageBg} ai="center" jc="center" px="$5">
      <Container maxWidth={440} centered>
        <YStack ai="center" gap="$4" w="100%">
          <SuccessHero compact />
          {!planSchedule && !effectiveAt && !!planLabel && <PlanPill label={planLabel} />}
          <YStack ai="center" gap="$2" w="100%">
            <PageTitle color={C.text} ta="center">
              {title}
            </PageTitle>

            {planSchedule ? (
              <PlanScheduleCard {...planSchedule} />
            ) : effectiveAt ? (
              <YStack ai="center" gap="$3" w="100%">
                {!!planLabel && <PlanPill label={planLabel} />}
                <BodyText color={C.muted} ta="center" lineHeight={24}>
                  Your new plan is active as of
                </BodyText>
                <DateHighlight label={effectiveAt} />
              </YStack>
            ) : (
              <>
                {!!message && (
                  <BodyText color={C.muted} ta="center" lineHeight={24}>
                    {message}
                  </BodyText>
                )}
                {billingHighlight ? (
                  <YStack mt="$3" w="100%" ai="center" gap="$2">
                    <CaptionText color={C.muted} ta="center" fontWeight="600">
                      {billingHighlight.prefix}
                    </CaptionText>
                    <DateHighlight label={billingHighlight.date} />
                  </YStack>
                ) : paymentCaption ? (
                  <YStack mt="$2" w="100%" ai="center">
                    <DateHighlight label={paymentCaption} />
                  </YStack>
                ) : null}
              </>
            )}
          </YStack>
          <YStack w="100%" mt="$2">
            <PrimaryButton onPress={onPrimary}>{primaryLabel}</PrimaryButton>
          </YStack>
        </YStack>
      </Container>
    </YStack>
  );
}

function PlanScheduleCard({
  currentPlan,
  newPlan,
  switchDate,
  align = 'center',
}: {
  currentPlan: string;
  newPlan: string;
  switchDate: string;
  align?: 'center' | 'left';
}) {
  const centered = align === 'center';
  const ta = centered ? 'center' : 'left';
  return (
    <YStack w="100%" mt="$3" gap="$4" ai={centered ? 'center' : 'flex-start'}>
      <YStack ai={centered ? 'center' : 'flex-start'} gap="$2" w="100%">
        <CaptionText
          color={C.muted}
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing={1}
          ta={ta}
        >
          Switches on
        </CaptionText>
        <SubtitleText color={C.text} fontWeight="700" ta={ta} lineHeight={24}>
          {switchDate}
        </SubtitleText>
      </YStack>

      <XStack
        ai="center"
        jc={centered ? 'center' : 'flex-start'}
        gap="$2.5"
        flexWrap="wrap"
        px={centered ? '$2' : 0}
        w="100%"
      >
        <BodyText color={C.muted} ta={ta}>
          {currentPlan}
        </BodyText>
        <Ionicons name="arrow-forward" size={16} color={C.muted} />
        <BodyText color={C.text} fontWeight="700" ta={ta}>
          {newPlan}
        </BodyText>
      </XStack>

      <BodyText color={C.muted} ta={ta} lineHeight={22} px={centered ? '$1' : 0}>
        Apple applies this type of change at the end of your current billing
        period, not immediately.
      </BodyText>
    </YStack>
  );
}

export { PlanScheduleCard };

function DateHighlight({ label }: { label: string }) {
  return (
    <SubtitleText color={C.text} fontWeight="700" ta="center" lineHeight={24}>
      {label}
    </SubtitleText>
  );
}

function OnboardingScreen({
  planLabel,
  nextPayment,
  purchased,
  onComplete,
  onSkip,
}: OnboardingProps) {
  const billingLine = nextPayment
    ? purchased?.isTrial
      ? `Free trial · first payment ${nextPayment}`
      : `Renews ${nextPayment}`
    : null;

  return (
    <YStack f={1} bg={C.pageBg}>
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 48 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Accent hero — the thank-you lives here, not on a separate screen */}
        <YStack
          bg={C.headerBg}
          pt="$10"
          pb="$8"
          px="$5"
          borderBottomLeftRadius={28}
          borderBottomRightRadius={28}
          overflow="hidden"
        >
          <YStack
            position="absolute"
            top={-40}
            right={-40}
            width={160}
            height={160}
            br={80}
            bg="rgba(228, 87, 46, 0.18)"
          />
          <YStack
            position="absolute"
            bottom={-20}
            left={-30}
            width={100}
            height={100}
            br={50}
            bg="rgba(36, 171, 226, 0.12)"
          />

          <Container maxWidth={520} centered>
            <YStack ai="center" gap="$3">
              <YStack
                width={80}
                height={80}
                br={40}
                bg="rgba(255,255,255,0.12)"
                ai="center"
                jc="center"
                borderWidth={2}
                borderColor="rgba(0, 177, 118, 0.6)"
              >
                <Ionicons name="checkmark-circle" size={52} color={SUCCESS} />
              </YStack>

              <CaptionText
                color="rgba(255,255,255,0.65)"
                fontWeight="700"
                letterSpacing={1.4}
                textTransform="uppercase"
                ta="center"
              >
                Membership active
              </CaptionText>

              <PageTitle color="#FFFFFF" ta="center" fontSize={28}>
                Welcome to Open Studio
              </PageTitle>

              {!!planLabel && (
                <XStack
                  bg="rgba(255,255,255,0.14)"
                  px="$3.5"
                  py="$2"
                  br="$10"
                  borderWidth={1}
                  borderColor="rgba(255,255,255,0.2)"
                >
                  <SubtitleText color="#FFFFFF" fontWeight="600">
                    {planLabel}
                  </SubtitleText>
                </XStack>
              )}

              <BodyText color="rgba(255,255,255,0.78)" ta="center" lineHeight={22}>
                {billingLine ??
                  'Every course, live session, and the full community is yours now.'}
              </BodyText>
            </YStack>
          </Container>
        </YStack>

        {/* Setup — only shown because this user has no dashboard yet */}
        <Container maxWidth={520} centered px="$5" pt="$6">
          <YStack
            bg={C.cardBg}
            br="$6"
            p="$5"
            borderWidth={1}
            borderColor={C.border}
            gap="$1"
            style={{
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.08,
              shadowRadius: 24,
              elevation: 4,
            }}
          >
            <XStack ai="center" gap="$2" mb="$1">
              <YStack width={8} height={8} br={4} bg={C.accent} />
              <CaptionText
                color={C.accent}
                fontWeight="700"
                letterSpacing={1.1}
                textTransform="uppercase"
              >
                One quick step
              </CaptionText>
            </XStack>

            <FindDashboardSurveyModal
              embedded
              tone="onboarding"
              open
              welcomeHeader={{
                title: 'Set your dashboard',
                subtitle: 'Pick your instrument and level, and we open the app on lessons made for you.',
              }}
              onOpenChange={() => {}}
              onSuccess={onComplete}
              onSkip={onSkip}
            />
          </YStack>
        </Container>
      </ScrollView>
    </YStack>
  );
}

function SuccessHero({ compact }: { compact?: boolean }) {
  const size = compact ? 72 : 88;
  return (
    <YStack
      width={size}
      height={size}
      br={size / 2}
      bg={C.cardBg}
      ai="center"
      jc="center"
      borderWidth={2}
      borderColor={SUCCESS}
      style={{
        shadowColor: SUCCESS,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 12,
        elevation: 3,
      }}
    >
      <Ionicons name="checkmark-circle" size={compact ? 48 : 56} color={SUCCESS} />
    </YStack>
  );
}

function PlanPill({ label }: { label: string }) {
  return (
    <XStack
      bg={C.cardBg}
      px="$3"
      py="$1.5"
      br="$10"
      borderWidth={1}
      borderColor={C.border}
    >
      <CaptionText color={C.text} fontWeight="600">
        {label}
      </CaptionText>
    </XStack>
  );
}

function PrimaryButton({
  children,
  onPress,
}: {
  children: React.ReactNode;
  onPress: () => void;
}) {
  return (
    <Button
      onPress={onPress}
      bg={C.accent}
      color={C.cardBg}
      fontWeight="700"
      fontSize="$4"
      br="$4"
      h={52}
      w="100%"
      pressStyle={{ opacity: 0.88 }}
      hoverStyle={{ opacity: 0.92 }}
    >
      {children}
    </Button>
  );
}
